import React, { useCallback, useEffect, useState } from 'react';
import { adminAPI } from '../../services/api';

const PLANS = [
  { id: 'monthly', label: 'Monthly', days: 30 },
  { id: 'annual', label: 'Annual', days: 365 },
];

const roleLabel = (role) => ({
  admin: 'Admin',
  lead_chitrakar: 'Lead Chitrakar',
  kalakar: 'Kalakar',
  artist: 'Artist',
  user: 'Buyer',
}[role] || role);

const formatDate = (value) => (value ? new Date(value).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—');

export const MembersPremiumPanel = () => {
  const [view, setView] = useState('members');
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('all');
  const [members, setMembers] = useState([]);
  const [summary, setSummary] = useState({ total_premium: 0, total_free: 0 });
  const [ledger, setLedger] = useState([]);
  const [ledgerSummary, setLedgerSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [grantTarget, setGrantTarget] = useState(null);
  const [grantForm, setGrantForm] = useState({ plan: 'monthly', duration_days: 30 });
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');

  const loadMembers = useCallback(async () => {
    setLoading(true);
    try {
      const data = await adminAPI.listMembers({ search, status });
      setMembers(data.members || []);
      setSummary({ total_premium: data.total_premium || 0, total_free: data.total_free || 0 });
    } catch (e) {
      setMessage(e.message || 'Failed to load members');
    } finally {
      setLoading(false);
    }
  }, [search, status]);

  const loadLedger = useCallback(async () => {
    setLoading(true);
    try {
      const data = await adminAPI.getPaymentLedger({ search });
      setLedger(data.payments || []);
      setLedgerSummary(data.summary || null);
    } catch (e) {
      setMessage(e.message || 'Failed to load payments');
    } finally {
      setLoading(false);
    }
  }, [search]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (view === 'members') loadMembers();
      else loadLedger();
    }, 250);
    return () => clearTimeout(timer);
  }, [view, loadMembers, loadLedger]);

  const openGrant = (member) => {
    setGrantTarget(member);
    setGrantForm({ plan: 'monthly', duration_days: 30 });
    setMessage('');
  };

  const grant = async () => {
    setBusy(true);
    try {
      const res = await adminAPI.grantMembership(grantTarget.id, grantForm.plan, Number(grantForm.duration_days));
      setMessage(res.message || 'Premium granted');
      setGrantTarget(null);
      await loadMembers();
    } catch (e) {
      setMessage(e.message || 'Failed to grant premium');
    } finally {
      setBusy(false);
    }
  };

  const revoke = async (member) => {
    if (!window.confirm(`Remove premium from ${member.full_name || member.email}?`)) return;
    setBusy(true);
    try {
      await adminAPI.revokeMembership(member.id);
      setMessage('Premium removed');
      await loadMembers();
    } catch (e) {
      setMessage(e.message || 'Failed to revoke premium');
    } finally {
      setBusy(false);
    }
  };

  const clearLock = async (userId) => {
    setBusy(true);
    try {
      await adminAPI.clearPaymentLock(userId);
      setMessage('Payment lock cleared — the user can pay again');
      await loadLedger();
    } catch (e) {
      setMessage(e.message || 'Failed to clear lock');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="premium-panel">
      <div className="bg-white rounded-xl shadow-sm p-5">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="text-xl font-bold text-gray-900">Premium & Payments</h2>
            <p className="text-sm text-gray-500">Enable premium for any account and review every payment attempt</p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setView('members')}
              className={`px-4 py-2 rounded-lg text-sm font-medium ${view === 'members' ? 'bg-[#0b1b33] text-white' : 'bg-gray-100 text-gray-700'}`}
              data-testid="premium-tab-members"
            >
              Accounts
            </button>
            <button
              onClick={() => setView('ledger')}
              className={`px-4 py-2 rounded-lg text-sm font-medium ${view === 'ledger' ? 'bg-[#0b1b33] text-white' : 'bg-gray-100 text-gray-700'}`}
              data-testid="premium-tab-ledger"
            >
              Payment Ledger
            </button>
          </div>
        </div>

        <div className="mt-4 flex flex-wrap gap-3">
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={view === 'members' ? 'Search by name, email or city' : 'Search by name, email, order or payment id'}
            className="flex-1 min-w-[220px] px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#c9a227] focus:border-transparent"
            data-testid="premium-search-input"
          />
          {view === 'members' && (
            <div className="flex gap-2">
              {[['all', 'All'], ['premium', 'Premium'], ['free', 'No premium']].map(([key, text]) => (
                <button
                  key={key}
                  onClick={() => setStatus(key)}
                  className={`px-3 py-2 rounded-lg text-sm font-medium ${status === key ? 'bg-[#c9a227] text-[#0b1b33]' : 'bg-gray-100 text-gray-700'}`}
                  data-testid={`premium-filter-${key}`}
                >
                  {text}
                </button>
              ))}
            </div>
          )}
        </div>

        {message && (
          <p className="mt-3 text-sm text-[#0b1b33] bg-amber-50 border border-amber-200 rounded-lg px-3 py-2" data-testid="premium-panel-message">
            {message}
          </p>
        )}

        {view === 'members' ? (
          <p className="mt-3 text-xs text-gray-500" data-testid="premium-counts">
            {summary.total_premium} premium • {summary.total_free} without premium
          </p>
        ) : ledgerSummary && (
          <p className="mt-3 text-xs text-gray-500" data-testid="ledger-summary">
            {ledgerSummary.completed} successful • {ledgerSummary.failed} failed • {ledgerSummary.pending} locked now • ₹{(ledgerSummary.collected || 0).toLocaleString('en-IN')} collected
          </p>
        )}
      </div>

      {loading ? (
        <div className="bg-white rounded-xl p-8 text-center text-gray-500">Loading...</div>
      ) : view === 'members' ? (
        <div className="bg-white rounded-xl shadow-sm divide-y divide-gray-100" data-testid="premium-member-list">
          {members.length === 0 && <p className="p-6 text-center text-gray-500">No accounts found</p>}
          {members.map((member) => (
            <div key={member.id} className="p-4 flex flex-wrap items-center gap-4" data-testid={`member-row-${member.id}`}>
              <div className="w-11 h-11 rounded-full bg-gray-100 overflow-hidden flex items-center justify-center shrink-0">
                {member.avatar
                  ? <img src={member.avatar} alt="" className="w-full h-full object-cover" />
                  : <span className="text-sm font-semibold text-gray-500">{(member.full_name || member.email || '?').charAt(0).toUpperCase()}</span>}
              </div>

              <div className="flex-1 min-w-[180px]">
                <p className="font-semibold text-gray-900">{member.full_name || 'Unnamed'}</p>
                <p className="text-xs text-gray-500">{member.email}</p>
                <div className="mt-1 flex flex-wrap items-center gap-2">
                  <span className="px-2 py-0.5 rounded-full bg-gray-100 text-xs text-gray-700">{roleLabel(member.role)}</span>
                  {member.is_lifetime_member ? (
                    <span className="px-2 py-0.5 rounded-full bg-[#0b1b33] text-xs text-[#c9a227] font-medium">Premium • Lifetime (Admin)</span>
                  ) : member.is_member ? (
                    <span className="px-2 py-0.5 rounded-full bg-green-100 text-xs text-green-700 font-medium">
                      Premium · {member.membership_plan || 'member'} · {member.days_left} days left
                    </span>
                  ) : (
                    <span className="px-2 py-0.5 rounded-full bg-gray-100 text-xs text-gray-600">No premium</span>
                  )}
                </div>
              </div>

              <div className="text-xs text-gray-500 min-w-[110px]">
                {member.is_lifetime_member ? 'Never expires' : member.is_member ? `Till ${formatDate(member.membership_expiry)}` : '—'}
              </div>

              <div className="flex gap-2">
                <button
                  onClick={() => openGrant(member)}
                  disabled={busy || member.is_lifetime_member}
                  className="px-3 py-2 rounded-lg bg-[#c9a227] text-[#0b1b33] text-sm font-semibold hover:bg-[#b8931f] disabled:opacity-40"
                  data-testid={`grant-premium-${member.id}`}
                >
                  {member.is_member ? 'Extend' : 'Enable Premium'}
                </button>
                {member.is_member && !member.is_lifetime_member && (
                  <button
                    onClick={() => revoke(member)}
                    disabled={busy}
                    className="px-3 py-2 rounded-lg bg-red-50 text-red-600 text-sm font-medium hover:bg-red-100 disabled:opacity-40"
                    data-testid={`revoke-premium-${member.id}`}
                  >
                    Revoke
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-sm divide-y divide-gray-100 overflow-hidden" data-testid="payment-ledger-list">
          {ledger.length === 0 && <p className="p-6 text-center text-gray-500">No payment records yet</p>}
          {ledger.map((row) => (
            <div key={row.id || row.razorpay_order_id} className="p-4 flex flex-wrap items-center gap-4" data-testid={`ledger-row-${row.id || row.razorpay_order_id}`}>
              <div className="flex-1 min-w-[200px]">
                <p className="font-semibold text-gray-900">{row.user_name || row.user_email || 'Unknown user'}</p>
                <p className="text-xs text-gray-500">{row.plan_name || row.plan_type} • {formatDate(row.created_at)}</p>
                {row.razorpay_order_id && <p className="text-[11px] text-gray-400 font-mono">{row.razorpay_order_id}</p>}
                {row.razorpay_payment_id && <p className="text-[11px] text-gray-400 font-mono">{row.razorpay_payment_id}</p>}
                {row.failure_reason && <p className="text-xs text-red-500">{row.failure_reason}</p>}
              </div>

              <div className="text-sm font-semibold text-gray-900 min-w-[90px]">
                {row.payment_method === 'admin_grant' ? 'Admin grant' : `₹${Number(row.amount || 0).toLocaleString('en-IN')}`}
              </div>

              <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                row.status === 'completed' ? 'bg-green-100 text-green-700'
                  : row.status === 'failed' ? 'bg-red-100 text-red-600'
                    : ['cancelled', 'expired'].includes(row.status) ? 'bg-gray-100 text-gray-600'
                      : 'bg-amber-100 text-amber-700'
              }`}>
                {row.status}{row.recovered ? ' (recovered)' : ''}
              </span>

              {row.new_expiry && (
                <span className="text-xs text-gray-500">
                  {row.previous_expiry ? 'Extended to' : 'Valid till'} {formatDate(row.new_expiry)}
                </span>
              )}

              {row.lock_active && (
                <button
                  onClick={() => clearLock(row.user_id)}
                  disabled={busy}
                  className="px-3 py-1.5 rounded-lg bg-gray-100 text-gray-700 text-xs font-medium hover:bg-gray-200 disabled:opacity-40"
                  data-testid={`clear-lock-${row.user_id}`}
                >
                  Clear 10-min lock
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {grantTarget && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/50 px-4" data-testid="grant-premium-modal">
          <div className="bg-white rounded-2xl w-full max-w-md overflow-hidden">
            <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
              <div>
                <h3 className="font-semibold text-gray-900">Enable Premium</h3>
                <p className="text-xs text-gray-500">{grantTarget.full_name || grantTarget.email}</p>
              </div>
              <button onClick={() => setGrantTarget(null)} className="text-gray-400 hover:text-gray-700">✕</button>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Plan</label>
                <select
                  value={grantForm.plan}
                  onChange={(e) => {
                    const plan = PLANS.find((p) => p.id === e.target.value);
                    setGrantForm({ plan: e.target.value, duration_days: plan ? plan.days : grantForm.duration_days });
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  data-testid="grant-plan-select"
                >
                  {PLANS.map((p) => <option key={p.id} value={p.id}>{p.label} ({p.days} days)</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Duration (days)</label>
                <input
                  type="number"
                  min={1}
                  value={grantForm.duration_days}
                  onChange={(e) => setGrantForm({ ...grantForm, duration_days: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  data-testid="grant-duration-input"
                />
              </div>
              {grantTarget.is_member && (
                <p className="text-xs text-green-700 bg-green-50 border border-green-200 rounded-lg px-3 py-2">
                  These days will be added on top of the existing membership (expires {formatDate(grantTarget.membership_expiry)}).
                </p>
              )}
              <div className="flex gap-3">
                <button
                  onClick={grant}
                  disabled={busy}
                  className="flex-1 py-2.5 rounded-lg bg-[#c9a227] text-[#0b1b33] font-semibold hover:bg-[#b8931f] disabled:opacity-50"
                  data-testid="confirm-grant-premium"
                >
                  {busy ? 'Saving...' : 'Confirm'}
                </button>
                <button
                  onClick={() => setGrantTarget(null)}
                  disabled={busy}
                  className="px-5 py-2.5 rounded-lg bg-gray-100 text-gray-700 font-medium"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MembersPremiumPanel;
